import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { checkActionExecution } from "@redotech/redo-web/actions/action-definition";
import {
  supportTicketActionIds,
  type SupportTicketActionId,
} from "@redotech/redo-web/actions/support-ticket-actions/action-ids";
import {
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/support-ticket-actions/scope-trees";
import { RedoCommandShortcut } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-shortcut";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Flex } from "@redotech/redo-web/flex";
import { getShortcutDisplayParts } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { AssigneeSelectorDropdownContent } from "../components/assignee-selector-dropdown-content";
import { SnoozeCustomModal } from "../components/snooze-custom-modal";
import { TagSelectorDropdownContent } from "../components/tag-selector-dropdown-content";
import { getAction } from "./action-registry";
import { DeleteSupportTicketConfirmModal } from "./delete-support-ticket-confirm-modal";
import { ScopeMenu } from "./scope-menu";
import {
  useAddTagSupportTicketAction,
  useAssignSupportTicketAction,
  useIsSupportTicketSpam,
  useSnoozeSupportTicketAction,
  useSupportTicketActionHandlers,
  type SupportTicketActionHandlerMap,
} from "./use-support-ticket-actions";

const STATUS_ACTION_IDS = [
  supportTicketActionIds.close,
  supportTicketActionIds.reopen,
  supportTicketActionIds.markInProgress,
  supportTicketActionIds.archive,
] as const;

const READ_ACTION_IDS = [
  supportTicketActionIds.markRead,
  supportTicketActionIds.markUnread,
] as const;

const DANGER_ACTION_IDS = [supportTicketActionIds.delete] as const;

export interface SupportTicketActionsDropdownProps {
  supportTicketId: SupportTicketId;
  supportTicket: SupportTicketListItemResponse;
  isOpen: boolean;
  onClose: () => void;
  onMergeTicket?: () => void;
  onUnmergeTicket?: () => void;
  align?: "start" | "center" | "end";
}

export const SupportTicketActionsDropdown = memo(
  function SupportTicketActionsDropdown({
    supportTicketId,
    supportTicket,
    isOpen,
    onClose,
    onMergeTicket,
    onUnmergeTicket,
    align = "end",
  }: SupportTicketActionsDropdownProps) {
    const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
    const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const snoozeAction = useSnoozeSupportTicketAction();
    const isSpam = useIsSupportTicketSpam(supportTicket);
    const handlers = useSupportTicketActionHandlers(
      supportTicketId,
      onMergeTicket,
      onUnmergeTicket,
    );

    const handlersWithCustomSnooze = useMemo(
      () => ({
        ...handlers,
        [supportTicketActionIds.snoozeCustom]: () => {
          setCustomSnoozeOpen(true);
          onClose();
        },
      }),
      [handlers, onClose],
    );

    const handlersWithDeleteConfirm = useMemo(
      () => ({
        ...handlers,
        [supportTicketActionIds.delete]: () => {
          setDeleteConfirmOpen(true);
        },
      }),
      [handlers],
    );

    const handleDeleteConfirm = useCallback(() => {
      handlers[supportTicketActionIds.delete]?.();
      setDeleteConfirmOpen(false);
    }, [handlers]);

    const handleDeleteCancel = useCallback(() => {
      setDeleteConfirmOpen(false);
    }, []);

    const addTagSupportTicketAction = useAddTagSupportTicketAction();
    const handleTagsChanged = useCallback(
      (
        newTagIds: Parameters<
          typeof addTagSupportTicketAction.execute
        >[1]["tagIds"],
      ) => {
        void addTagSupportTicketAction.execute(supportTicketId, {
          tagIds: newTagIds,
        });
      },
      [addTagSupportTicketAction, supportTicketId],
    );

    const bindings = useMemo(() => {
      if (!supportTicketId || !supportTicket) {
        return [];
      }

      const simpleActionIds = [
        ...STATUS_ACTION_IDS,
        onUnmergeTicket
          ? supportTicketActionIds.unmergeTicket
          : supportTicketActionIds.mergeTicket,
        ...READ_ACTION_IDS,
        ...DANGER_ACTION_IDS,
      ];

      const actionBindings = simpleActionIds
        .map((id) => {
          const action = getAction(id);
          if (!action.shortcut) {
            return null;
          }
          return {
            shortcut: action.shortcut,
            handler: () => {
              const check = checkActionExecution(action, supportTicket);
              if (!check.canExecute) {
                return;
              }
              handlersWithDeleteConfirm[id]?.();
              onClose();
            },
          };
        })
        .filter(
          (binding): binding is NonNullable<typeof binding> => binding !== null,
        );

      const setPriorityAction = getAction(supportTicketActionIds.setPriority);
      const setSnoozeAction = getAction(supportTicketActionIds.snooze);
      const assignTriggerAction = getAction(supportTicketActionIds.assign);
      const submenuTriggerBindings = [
        setPriorityAction.shortcut
          ? {
              shortcut: setPriorityAction.shortcut,
              handler: () => setOpenSubmenuId("priority"),
            }
          : null,
        setSnoozeAction.shortcut
          ? {
              shortcut: setSnoozeAction.shortcut,
              handler: () => setOpenSubmenuId("snooze"),
            }
          : null,
        assignTriggerAction.shortcut
          ? {
              shortcut: assignTriggerAction.shortcut,
              handler: () => {
                const check = checkActionExecution(
                  assignTriggerAction,
                  supportTicket,
                );
                if (!check.canExecute) {
                  return;
                }
                setOpenSubmenuId("assign");
              },
            }
          : null,
      ].filter(
        (binding): binding is NonNullable<typeof binding> => binding !== null,
      );

      return [...actionBindings, ...submenuTriggerBindings];
    }, [
      supportTicket,
      supportTicketId,
      onClose,
      handlersWithDeleteConfirm,
      onUnmergeTicket,
    ]);

    useKeyboardShortcuts({
      bindings,
      scope: "actionMenu",
      enabled: isOpen && openSubmenuId === null,
    });

    const renderActionItem = useCallback(
      (
        actionId: SupportTicketActionId,
        actionHandlers: SupportTicketActionHandlerMap,
        ticket: SupportTicketListItemResponse,
        handleClose: () => void,
        className?: string,
      ) => {
        const action = getAction(actionId);
        const check = checkActionExecution(action, ticket);
        if (action.isVisible && !action.isVisible(ticket)) {
          return null;
        }
        return (
          <DropdownMenuItem
            className={className}
            disabled={!check.canExecute}
            key={actionId}
            onClick={() => {
              actionHandlers[actionId]?.();
              handleClose();
            }}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {action.Icon && <action.Icon className="h-4 w-4" />}
                <span>{action.title}</span>
              </div>
              {action.shortcut && (
                <RedoCommandShortcut
                  hotkey={getShortcutDisplayParts(action.shortcut)}
                />
              )}
            </div>
          </DropdownMenuItem>
        );
      },
      [],
    );

    const setPriorityAction = getAction(supportTicketActionIds.setPriority);
    const isPriorityVisible =
      setPriorityAction.isVisible?.(supportTicket) !== false;
    const isPriorityEnabled = checkActionExecution(
      setPriorityAction,
      supportTicket,
    ).canExecute;

    const setSnoozeAction = getAction(supportTicketActionIds.snooze);
    const isSnoozeVisible =
      setSnoozeAction.isVisible?.(supportTicket) !== false;
    const isSnoozeEnabled = checkActionExecution(
      setSnoozeAction,
      supportTicket,
    ).canExecute;

    const addTagAction = getAction(supportTicketActionIds.addTag);
    const isAddTagVisible = addTagAction.isVisible?.(supportTicket) !== false;

    const assignAction = useAssignSupportTicketAction();
    const isAssignVisible = assignAction.isVisible?.(supportTicket) !== false;
    const isAssignEnabled = checkActionExecution(
      assignAction,
      supportTicket,
    ).canExecute;

    return (
      <>
        <DropdownMenuContent
          align={align}
          className="[&_[data-slot=dropdown-menu-item]]:text-xs [&_[data-slot=dropdown-menu-sub-trigger]]:text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_ACTION_IDS.map((id) =>
            renderActionItem(id, handlers, supportTicket, onClose),
          )}

          {renderActionItem(
            onUnmergeTicket
              ? supportTicketActionIds.unmergeTicket
              : supportTicketActionIds.mergeTicket,
            handlers,
            supportTicket,
            onClose,
          )}

          {renderActionItem(
            isSpam
              ? supportTicketActionIds.unmarkSpam
              : supportTicketActionIds.markSpam,
            handlers,
            supportTicket,
            onClose,
          )}

          <DropdownMenuSeparator />

          {READ_ACTION_IDS.map((id) =>
            renderActionItem(id, handlers, supportTicket, onClose),
          )}

          <DropdownMenuSeparator />

          {isPriorityVisible && (
            <DropdownMenuSub
              onOpenChange={(open) =>
                setOpenSubmenuId(open ? "priority" : null)
              }
              open={openSubmenuId === "priority"}
            >
              <DropdownMenuSubTrigger disabled={!isPriorityEnabled}>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {setPriorityAction.Icon && (
                      <setPriorityAction.Icon className="h-4 w-4" />
                    )}
                    <span>{setPriorityAction.title}</span>
                  </div>
                  {setPriorityAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(
                        setPriorityAction.shortcut,
                      )}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <ScopeMenu
                  depth={1}
                  handlers={handlers}
                  onClose={onClose}
                  scope={priorityScope}
                  supportTicket={supportTicket}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {isSnoozeVisible && (
            <DropdownMenuSub
              onOpenChange={(open) => setOpenSubmenuId(open ? "snooze" : null)}
              open={openSubmenuId === "snooze"}
            >
              <DropdownMenuSubTrigger disabled={!isSnoozeEnabled}>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {setSnoozeAction.Icon && (
                      <setSnoozeAction.Icon className="h-4 w-4" />
                    )}
                    <span>{setSnoozeAction.title}</span>
                  </div>
                  {setSnoozeAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(setSnoozeAction.shortcut)}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <ScopeMenu
                  depth={1}
                  handlers={handlersWithCustomSnooze}
                  onClose={onClose}
                  scope={snoozeScope}
                  supportTicket={supportTicket}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {isAssignVisible && (
            <DropdownMenuSub
              onOpenChange={(open) => setOpenSubmenuId(open ? "assign" : null)}
              open={openSubmenuId === "assign"}
            >
              <DropdownMenuSubTrigger disabled={!isAssignEnabled}>
                <Flex align="center" gap="xl" justify="space-between" w="full">
                  <Flex align="center" gap="md">
                    {assignAction.Icon && (
                      <assignAction.Icon className="h-4 w-4" />
                    )}
                    <Text>{assignAction.title}</Text>
                  </Flex>
                  {assignAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(assignAction.shortcut)}
                    />
                  )}
                </Flex>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <AssigneeSelectorDropdownContent
                  assigneeType={supportTicket.assigneeType}
                  mongoAssigneeId={supportTicket.mongoAssigneeId}
                  supportTicketId={supportTicketId}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {renderActionItem(
            supportTicketActionIds.assignToMe,
            handlers,
            supportTicket,
            onClose,
          )}

          {isAddTagVisible && (
            <DropdownMenuSub
              onOpenChange={(open) => setOpenSubmenuId(open ? "addTag" : null)}
              open={openSubmenuId === "addTag"}
            >
              <DropdownMenuSubTrigger>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {addTagAction.Icon && (
                      <addTagAction.Icon className="h-4 w-4" />
                    )}
                    <span>{addTagAction.title}</span>
                  </div>
                  {addTagAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(addTagAction.shortcut)}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <TagSelectorDropdownContent
                  accountId={supportTicket.accountId}
                  currentTagIds={supportTicket.tagIds}
                  onTagsChanged={handleTagsChanged}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          <DropdownMenuSeparator />

          {DANGER_ACTION_IDS.map((id) =>
            renderActionItem(
              id,
              handlersWithDeleteConfirm,
              supportTicket,
              onClose,
              "text-red-600 focus:text-red-600",
            ),
          )}
        </DropdownMenuContent>
        <DeleteSupportTicketConfirmModal
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          open={deleteConfirmOpen}
        />
        {customSnoozeOpen && (
          <SnoozeCustomModal
            onClose={() => setCustomSnoozeOpen(false)}
            onConfirm={(id, until) => {
              void snoozeAction.execute(id, { duration: "custom", until });
            }}
            supportTicketId={supportTicketId}
          />
        )}
      </>
    );
  },
);
